import { jsPDF } from 'jspdf';

export type ProcessOptions = {
  maxWidth?: number;
  quality?: number;
  format?: 'jpeg' | 'webp';
};

const isImage = (file: File) => file.type.startsWith('image/');

const loadImage = (blob: Blob): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = URL.createObjectURL(blob);
});

const drawToCanvas = async (file: File, maxWidth: number, quality: number, format: 'jpeg' | 'webp'): Promise<{ dataUrl: string, width: number, height: number }> => {
  const img = await loadImage(file);
  const scale = img.width > maxWidth ? maxWidth / img.width : 1;
  const width = Math.round(img.width * scale);
  const height = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context unavailable');
  ctx.drawImage(img, 0, 0, width, height);
  const mime = format === 'webp' ? 'image/webp' : 'image/jpeg';
  const dataUrl = canvas.toDataURL(mime, quality);
  URL.revokeObjectURL(img.src);
  return { dataUrl, width, height };
};

export const processToPdf = async (file: File, opts: ProcessOptions = {}): Promise<File> => {
  if (!isImage(file)) {
    return file;
  }
  const { maxWidth = 1600, quality = 0.7, format = 'jpeg' } = opts;
  const { dataUrl, width, height } = await drawToCanvas(file, maxWidth, quality, format);

  // Create PDF using pixel units to preserve exact dimensions
  const pdf = new jsPDF({ unit: 'px', format: [width, height] });
  pdf.addImage(dataUrl, format.toUpperCase() as 'JPEG' | 'WEBP', 0, 0, width, height);
  const blob = pdf.output('blob');
  const name = file.name.replace(/\.[^.]+$/, '') + '.pdf';
  return new File([blob], name, { type: 'application/pdf' });
};

export const processManyToPdf = async (files: File[], opts: ProcessOptions = {}): Promise<File[]> => {
  const out: File[] = [];
  for (const f of files) {
    try {
      out.push(await processToPdf(f, opts));
    } catch (e) {
      // Fallback to original file if processing fails
      out.push(f);
    }
  }
  return out;
};